import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/pages/dashboard/components/DashboardLayout";
import { DashboardBooking, pastBookings, upcomingBookings } from "@/mocks/dashboard";

type Tab = "upcoming" | "past";

function formatDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function shortDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return {
    month: d.toLocaleDateString("en-US", { month: "short" }),
    day: d.getDate(),
    weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
  };
}

function BookingCard({
  b,
  onOpen,
}: {
  b: DashboardBooking;
  onOpen: (b: DashboardBooking) => void;
}) {
  const s = shortDate(b.date);
  const isUpcoming = b.status === "upcoming";
  return (
    <div
      onClick={() => onOpen(b)}
      className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row gap-4 md:gap-5 hover:border-background-300 transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-4 md:w-56 flex-shrink-0">
        <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-background-100 flex flex-col items-center justify-center border border-background-200 flex-shrink-0">
          <span className="text-[10px] font-medium text-foreground-600 uppercase">{s.month}</span>
          <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 leading-none">
            {s.day}
          </span>
          <span className="text-[10px] text-foreground-500 mt-0.5">{s.weekday}</span>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground-950">{b.time}</p>
          <p className="text-xs text-foreground-600 mt-0.5">{b.duration}</p>
        </div>
      </div>

      <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <img
            src={b.cleanerImage}
            alt={b.cleanerName}
            className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover object-top flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-md bg-primary-100">
                <i className={`${b.serviceIcon} text-primary-700 text-xs md:text-sm`}></i>
              </span>
              <h3 className="text-sm md:text-base font-medium text-foreground-950 truncate">{b.service}</h3>
              {isUpcoming && (
                <span className="inline-flex items-center gap-1 bg-primary-100 text-primary-800 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap">
                  <span className="w-1 h-1 rounded-full bg-primary-600"></span> Confirmed
                </span>
              )}
              {b.status === "completed" && (
                <span className="inline-flex items-center gap-1 bg-background-200 text-foreground-700 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap">
                  Completed
                </span>
              )}
            </div>
            <p className="text-xs text-foreground-600 mt-1 truncate">
              with {b.cleanerName} · {b.frequency}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0 justify-between md:justify-end">
          {b.rating && (
            <div className="hidden md:flex items-center gap-1 text-sm text-foreground-700">
              {Array.from({ length: 5 }).map((_, i) => (
                <i
                  key={i}
                  className={`${i < b.rating! ? "ri-star-fill text-secondary-500" : "ri-star-line text-background-300"} text-sm`}
                ></i>
              ))}
            </div>
          )}
          <p className="text-base font-semibold text-foreground-950 whitespace-nowrap">
            ${b.total}
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpen(b);
            }}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 cursor-pointer"
            aria-label="View details"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

function BookingDetailDrawer({
  booking,
  onClose,
}: {
  booking: DashboardBooking | null;
  onClose: () => void;
}) {
  if (!booking) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-foreground-950/40" onClick={onClose}></div>
      <div className="fixed top-0 right-0 z-50 h-screen w-full sm:max-w-md bg-background-50 overflow-y-auto animate-fade-up">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 px-6 py-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-foreground-950">
            Booking details
          </h3>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-700"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
              <i className={`${booking.serviceIcon} text-primary-700 text-lg`}></i>
            </span>
            <div>
              <p className="font-heading text-xl font-semibold text-foreground-950">
                {booking.service}
              </p>
              <p className="text-xs text-foreground-600">Booking #{booking.id}</p>
            </div>
          </div>

          <div className="bg-background-100 rounded-xl p-4 flex items-center gap-3">
            <img
              src={booking.cleanerImage}
              alt={booking.cleanerName}
              className="w-12 h-12 rounded-full object-cover object-top"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground-950">{booking.cleanerName}</p>
              <p className="text-xs text-foreground-600">Your cleaner</p>
            </div>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 text-foreground-700">
              <i className="ri-message-3-line"></i>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-foreground-600">Date</p>
              <p className="font-medium text-foreground-950 mt-0.5">{formatDate(booking.date)}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-600">Time</p>
              <p className="font-medium text-foreground-950 mt-0.5">{booking.time}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-600">Duration</p>
              <p className="font-medium text-foreground-950 mt-0.5">{booking.duration}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-600">Frequency</p>
              <p className="font-medium text-foreground-950 mt-0.5">{booking.frequency}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-foreground-600">Address</p>
              <p className="font-medium text-foreground-950 mt-0.5">{booking.address}</p>
            </div>
          </div>

          {booking.addOns.length > 0 && (
            <div>
              <p className="text-xs text-foreground-600 mb-2">Add-ons</p>
              <div className="flex flex-wrap gap-2">
                {booking.addOns.map((a) => (
                  <span
                    key={a}
                    className="inline-flex items-center gap-1.5 bg-secondary-100 text-secondary-900 px-3 py-1 rounded-full text-xs"
                  >
                    <i className="ri-add-line text-xs"></i> {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          {booking.review && (
            <div className="bg-background-100 rounded-xl p-4">
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`${i < (booking.rating ?? 0) ? "ri-star-fill text-secondary-500" : "ri-star-line text-background-300"} text-sm`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-foreground-800 italic leading-relaxed">
                "{booking.review}"
              </p>
            </div>
          )}

          <div className="border-t border-background-200 pt-4 flex items-center justify-between">
            <p className="text-sm text-foreground-600">Total paid</p>
            <p className="text-xl font-heading font-semibold text-foreground-950">
              ${booking.total}
            </p>
          </div>

          {booking.status === "upcoming" ? (
            <div className="flex gap-2">
              <button className="flex-1 bg-foreground-950 hover:bg-foreground-900 text-background-50 py-2.5 rounded-full text-sm font-medium whitespace-nowrap">
                Reschedule
              </button>
              <button className="flex-1 bg-background-100 hover:bg-background-200 text-foreground-800 py-2.5 rounded-full text-sm font-medium whitespace-nowrap">
                Cancel visit
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Link
                to="/booking"
                className="flex-1 text-center bg-primary-500 hover:bg-primary-600 text-background-50 py-2.5 rounded-full text-sm font-medium whitespace-nowrap"
              >
                Rebook this clean
              </Link>
              <button className="flex-1 bg-background-100 hover:bg-background-200 text-foreground-800 py-2.5 rounded-full text-sm font-medium whitespace-nowrap">
                Download receipt
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function DashboardBookingsPage() {
  const [tab, setTab] = useState<Tab>("upcoming");
  const [selected, setSelected] = useState<DashboardBooking | null>(null);

  const list = useMemo(() => (tab === "upcoming" ? upcomingBookings : pastBookings), [tab]);

  return (
    <DashboardLayout
      title="My bookings"
      subtitle="Everything past, present, and future."
      action={
        <Link
          to="/booking"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 pl-5 pr-2 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
        >
          New booking
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-50">
            <i className="ri-add-line text-primary-600 text-sm"></i>
          </span>
        </Link>
      }
    >
      <div className="space-y-5">
        {/* Tabs */}
        <div className="inline-flex items-center bg-background-50 border border-background-200 rounded-full px-1 py-1 w-full sm:w-auto">
          <button
            onClick={() => setTab("upcoming")}
            className={`flex-1 sm:flex-none px-4 sm:px-5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
              tab === "upcoming"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            Upcoming · {upcomingBookings.length}
          </button>
          <button
            onClick={() => setTab("past")}
            className={`flex-1 sm:flex-none px-4 sm:px-5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
              tab === "past"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            Past · {pastBookings.length}
          </button>
        </div>

        {list.length === 0 ? (
          <div className="bg-background-50 border border-background-200 rounded-2xl py-16 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-calendar-line text-2xl text-foreground-500"></i>
            </span>
            <p className="mt-4 font-heading text-lg text-foreground-950">No bookings yet</p>
            <p className="text-sm text-foreground-600 mt-1">Book your first clean to get going.</p>
            <Link
              to="/booking"
              className="mt-5 inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap"
            >
              Book a clean <i className="ri-arrow-right-up-line"></i>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((b) => (
              <BookingCard key={b.id} b={b} onOpen={setSelected} />
            ))}
          </div>
        )}
      </div>

      <BookingDetailDrawer booking={selected} onClose={() => setSelected(null)} />
    </DashboardLayout>
  );
}